import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { FilterValues } from "../../../data-model/filterValues";
import { PharmaStpFileModel } from "../../../data-model/nycbsPharmaOrder";
import { TableNames } from "../../../enums/tableNames";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { buildAsteraFilterPayload } from "../utils/buildAsteraFilterPayload";
import { AsteraOncologyExternalTableState } from "./useAsteraOncologyExternalTableData";
import {
  AsteraOncologyExternalFilterPayload,
  useFilterPayload,
} from "./useFilterPayload";

export interface UseNextPageDataFetchReturn {
  endIndex: number;
  setEndIndex: React.Dispatch<React.SetStateAction<number>>;
}

export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  asteraOncologyExternalState: AsteraOncologyExternalTableState,
  setAsteraOncologyExternalState: React.Dispatch<
    React.SetStateAction<AsteraOncologyExternalTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<PharmaStpFileModel[]>>,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number = 120,
): UseNextPageDataFetchReturn => {
  const { setTableDataForContext } = useTablesContext();
  const [endIndex, setEndIndex] = useState<number>(0);

  const lastRequestedPageRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);

  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setAsteraOncologyExternalStateRef = useRef(
    setAsteraOncologyExternalState,
  );
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setAsteraOncologyExternalStateRef.current = setAsteraOncologyExternalState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setAsteraOncologyExternalState,
    setTableData,
    setTableDataForContext,
  ]);

  const validFiltersKey = JSON.stringify(validFilters);
  useEffect(() => {
    lastRequestedPageRef.current = 0;
  }, [validFiltersKey, searchTerm, dateFromStart, dateFromEnd]);

  const shouldFetchMoreData = useMemo(() => {
    const currentPageRowCount =
      asteraOncologyExternalState.data?.row_count ?? 0;
    const currentPage = asteraOncologyExternalState.data?.page ?? 1;
    const totalPages = asteraOncologyExternalState.data?.total_pages ?? 1;
    const nextPageToFetch = currentPage + 1;

    const previousPagesRows = (currentPage - 1) * pageSize;
    const cumulativeRowsLoaded = previousPagesRows + currentPageRowCount;

    return (
      endIndex >= cumulativeRowsLoaded - 1 &&
      currentPage < totalPages &&
      !asteraOncologyExternalState.loading &&
      !asteraOncologyExternalState.showInlineLoader &&
      nextPageToFetch > lastRequestedPageRef.current
    );
  }, [
    endIndex,
    asteraOncologyExternalState.data?.row_count,
    asteraOncologyExternalState.data?.page,
    asteraOncologyExternalState.data?.total_pages,
    asteraOncologyExternalState.loading,
    asteraOncologyExternalState.showInlineLoader,
    pageSize,
  ]);

  const payloadFinal = useMemo<AsteraOncologyExternalFilterPayload>(() => {
    if (filterPayload === null) {
      return buildAsteraFilterPayload(
        validFilters,
        searchTerm,
        dateFromStart,
        dateFromEnd,
        pageSize,
      );
    }
    return filterPayload;
  }, [
    filterPayload,
    validFilters,
    dateFromStart,
    dateFromEnd,
    searchTerm,
    pageSize,
  ]);

  const payloadFinalWithPage =
    useMemo<AsteraOncologyExternalFilterPayload>(() => {
      return {
        ...payloadFinal,
        page: (asteraOncologyExternalState.data?.page ?? 0) + 1,
        request_id: uuidv4(),
      };
    }, [payloadFinal, asteraOncologyExternalState.data?.page]);

  const currentPage = asteraOncologyExternalState.data?.page ?? 0;

  const fetchNextPage = useCallback(() => {
    if (!isMountedRef.current) return;

    const nextPage = currentPage + 1;
    lastRequestedPageRef.current = nextPage;

    makePostCallForNextPagesDataFetch(
      payloadFinalWithPage,
      setAsteraOncologyExternalStateRef.current,
      setTableDataRef.current,
      isMountedRef,
    );
    setTableDataForContextRef.current(
      TableNames.ASTERA_ONCOLOGY_EXTERNAL_ORDERS,
      {
        lastBatchFetched: nextPage,
      },
    );
  }, [payloadFinalWithPage, currentPage]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
